import axios from 'axios';
import { Message, Loading } from 'element-ui';

// 创建axios实例
const service = axios.create({
  baseURL: '/api', // API基础路径，使用相对路径通过代理访问后端服务
  timeout: 15000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  },
  withCredentials: true, // 允许携带cookie
  responseType: 'json' // 响应数据类型
});

let loadingInstance = null;

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 显示加载动画
    loadingInstance = Loading.service({
      lock: true,
      text: '加载中...',
      background: 'rgba(0, 0, 0, 0.7)'
    });

    // 添加token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  error => {
    // 隐藏加载动画
    if (loadingInstance) {
      loadingInstance.close();
    }
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 隐藏加载动画
    if (loadingInstance) {
      loadingInstance.close();
    }

    const res = response.data;

    // 根据后端返回的状态码进行处理
    if (res.code !== 200 && res.code !== 0) {
      // 错误提示
      Message({
        message: res.message || '请求失败',
        type: 'error',
        duration: 5 * 1000
      });

      // 处理token过期等特殊情况
      if (res.code === 401) {
        // 清除token并跳转到登录页
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        window.location.href = '/login';
      }

      return Promise.reject(new Error(res.message || '请求失败'));
    } else {
      return res;
    }
  },
  error => {
    // 隐藏加载动画
    if (loadingInstance) {
      loadingInstance.close();
    }

    // 处理网络错误
    let errorMessage = '网络错误，请稍后重试';
    if (error.response) {
      switch (error.response.status) {
        case 400:
          errorMessage = '请求参数错误';
          break;
        case 401:
          errorMessage = '未授权，请重新登录';
          // 清除token并跳转到登录页
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          window.location.href = '/login';
          break;
        case 403:
          errorMessage = '拒绝访问';
          break;
        case 404:
          errorMessage = '请求的资源不存在';
          break;
        case 500:
          errorMessage = '服务器内部错误';
          break;
        default:
          errorMessage = error.response.data?.message || '请求失败';
      }
    }

    Message({
      message: errorMessage,
      type: 'error',
      duration: 5 * 1000
    });

    return Promise.reject(error);
  }
);

// 封装请求方法
export default {
  // GET请求
  get(url, params = {}) {
    return service({
      url,
      method: 'get',
      params
    });
  },

  // POST请求
  post(url, data = {}) {
    return service({
      url,
      method: 'post',
      data
    });
  },

  // PUT请求
  put(url, data = {}) {
    return service({
      url,
      method: 'put',
      data
    });
  },

  // DELETE请求
  delete(url, params = {}) {
    return service({
      url,
      method: 'delete',
      params
    });
  },

  // 上传文件
  upload(url, formData) {
    return service({
      url,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // 下载文件
  download(url, params = {}) {
    return service({
      url,
      method: 'get',
      params,
      responseType: 'blob'
    }).then(response => {
      // 处理文件下载
      const blob = new Blob([response], { type: response.type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // 从响应头获取文件名
      const contentDisposition = response.headers?.['content-disposition'];
      let filename = 'download';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.*)/);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1].replace(/"/g, ''));
        }
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      return { filename };
    });
  }
};

// API接口定义
export const api = {
  // 登录相关API
  getUserInfo: () => service.get('/admin/info'),
  
  // 首页相关接口
  home: {
    // 获取首页轮播图
    getBanners: () => service.get('/home/banners'),
    // 获取首页产品推荐
    getRecommendedProducts: () => service.get('/home/recommended-products'),
    // 获取首页案例推荐
    getRecommendedCases: () => service.get('/home/recommended-cases'),
    // 获取首页新闻
    getNews: () => service.get('/home/news'),
    // 获取公司简介
    getCompanyIntro: () => service.get('/home/company-intro'),
    // 获取首页统计数据
    getStats: () => service.get('/home/stats'),
    // 获取首页合作伙伴
    getPartners: () => service.get('/home/partners')
  },

  // 产品相关接口
  product: {
    // 获取产品列表
    getList: (params) => service.get('/product/list', { params }),
    // 获取产品详情
    getDetail: (id) => service.get(`/product/${id}`),
    // 获取上一个/下一个产品
    getAdjacent: (id) => service.get(`/product/${id}/adjacent`),
    // 产品搜索
    search: (keyword) => service.get('/products/search', { params: { keyword } })
  },

  // 产品优势相关接口
  productAdvantage: {
    // 获取产品优势列表
    getList: () => service.get('/product/advantage/list')
  },

  // 产品类别相关接口
  productCategory: {
    // 获取启用的产品类别
    getEnabled: () => service.get('/product/category/enabled'),
    // 获取产品类别树
    getTree: () => service.get('/product/category/tree')
  },

  // 案例相关接口
  case: {
    // 获取案例列表
    getList: (params) => service.get('/case/list', { params }),
    // 获取案例详情
    getDetail: (id) => service.get(`/case/${id}`)
  },

  // 轮播图相关API
  banner: {
    getList: (params) => service.get('/banner/list', { params }),
    getById: (id) => service.get(`/banner/${id}`),
    add: (data) => service.post('/banner', data),
    update: (data) => service.put('/banner', data),
    delete: (id) => service.delete(`/banner/${id}`)
  },
  
  // 首页内容相关API
  homeContent: {
    getLatest: () => service.get('/homeContent/latest'),
    add: (data) => service.post('/homeContent', data),
    update: (data) => service.put('/homeContent', data)
  },

  // 联系相关接口
  contact: {
    // 提交联系信息
    submit: (data) => service.post('/contact', data),
    // 获取常见问题
    getFaqList: () => service.get('/contact/faq/list'),
    // 获取联系列表
    getList: (params) => service.get('/contact/list', { params }),
    // 根据ID获取联系详情
    getById: (id) => service.get(`/contact/${id}`),
    // 更新联系信息
    update: (data) => service.put('/contact', data),
    // 删除联系信息
    delete: (id) => service.delete(`/contact/${id}`),
    // 批量删除联系信息
    batchDelete: (ids) => service.post('/contact/batchDelete', { ids })
  },

  // 关于我们相关接口
  about: {
    // 获取关于我们信息
    getInfo: () => service.get('/about'),
    // 获取发展历程
    getTimeline: () => service.get('/about/timeline'),
    // 获取企业文化
    getCulture: () => service.get('/about/culture'),
    // 获取团队成员
    getTeam: () => service.get('/about/team'),
    // 获取资质认证
    getCertifications: () => service.get('/about/certifications')
  },

  // 应用领域相关接口
  applicationField: {
    // 获取启用的应用领域
    getEnabled: () => service.get('/application/field/enabled')
  }
};
